import {
  DataTablePagination,
  DataTableSearch,
  Typography,
  DataTable,
  Button,
} from '@ui/components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useCallback, ReactNode, useState } from 'react';
import { cn } from '@ui/utils';

import { UseTableList } from './use-table-list';

export interface TableListProps<T> extends UseTableList<T> {
  onSelectedChange: (value: string[]) => void;
  selected?: string[];
  text: {
    selectedRows: (count: number) => string;
    tableNoResults: ReactNode | string;
    searchPlaceholder: string;
    removeButtonText: string;
    title: string;
  };
  className?: string;
  disabled?: boolean;
}

export function TableList<T>({
  onSelectedChange,
  selected: value,
  className,
  isLoading,
  disabled,
  table,
  text,
}: TableListProps<T>) {
  const { title, searchPlaceholder, removeButtonText, tableNoResults, selectedRows } = text;
  const [selected, setSelected] = useState<string[]>(value ?? []);

  useDeepCompareEffect(() => {
    setSelected(value ?? []);
  }, [value, setSelected]);

  const selectedItems = Object.keys(table.getState().rowSelection).map((key) => key);

  const handleRemove = useCallback(() => {
    onSelectedChange?.(selected.filter((item) => !selectedItems.includes(item)));
    table.setRowSelection({});
    table.setPageIndex(0);
  }, [table, selectedItems, selected, onSelectedChange]);

  return (
    <div
      className={cn(
        'bg-table-background rounded-lg overflow-hidden border border-border-weak',
        className,
      )}
    >
      <div className="ml-auto flex items-center justify-between gap-4 p-4">
        <Typography variant="labelMediumMedium">
          {selectedItems.length > 0 ? selectedRows(selectedItems.length) : title}
        </Typography>
        <DataTablePagination
          pageIndex={table.getState().pagination.pageIndex}
          pageSize={table.getState().pagination.pageSize}
          prevPageDisabled={!table.getCanPreviousPage()}
          nextPageDisabled={!table.getCanNextPage()}
          prevPage={() => table.previousPage()}
          nextPage={() => table.nextPage()}
          totalRows={table.getRowCount()}
        />
      </div>
      <div className="px-4 pb-3">
        <DataTableSearch
          value={table.getState().globalFilter}
          onChange={table.setGlobalFilter}
          placeholder={searchPlaceholder}
          className="w-61"
        />
      </div>

      {!disabled && !!selectedItems.length && (
        <div>
          <Button onClick={handleRemove} variant="ghost" size="small">
            {removeButtonText}
          </Button>
        </div>
      )}

      <DataTable
        noResultsComponent={<div className="text-center">{tableNoResults}</div>}
        columnsLength={table.getVisibleFlatColumns().length}
        className="max-h-full overflow-auto"
        isLoading={isLoading ?? false}
        table={table}
      />
    </div>
  );
}
